import React, { useState, useEffect } from "react";
import {
    Box,
    Input,
    Button,
    Text,
    VStack,
    Heading,
    Container,
    Spinner,
    Alert,
    AlertIcon,
    useToast,
    Select,
    Flex,
    Badge,
    HStack,
    IconButton,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp } from "lucide-react";
import baseUrl from "Base_Url/baseUrl";

const TargetSet = () => {
    const [talukas, setTalukas] = useState([]);
    const [fileTypes, setFileTypes] = useState([]);
    const [selectedFileType, setSelectedFileType] = useState(null);
    const [selectedFileTypeId, setSelectedFileTypeId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingFileTypes, setLoadingFileTypes] = useState(false);
    const [error, setError] = useState(null);
    const [targets, setTargets] = useState({});
    const [submitting, setSubmitting] = useState({});
    const [financialYear, setFinancialYear] = useState("");
    const toast = useToast();

    const schemeId = "68b9513ffa74b7a86b7aa38a";

    useEffect(() => {
        fetchFileTypes();
    }, []);

    const fetchFileTypes = async () => {
        try {
            setLoadingFileTypes(true);
            const apiUrl = `${baseUrl}/master/all-file-type?scheme=${schemeId}`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            }
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("File types response:", result);

            let fileTypesArray = [];
            if (result.data && result.data.data && Array.isArray(result.data.data)) {
                fileTypesArray = result.data.data;
            } else if (result.data && Array.isArray(result.data)) {
                fileTypesArray = result.data;
            } else if (Array.isArray(result)) {
                fileTypesArray = result;
            }

            setFileTypes(fileTypesArray);

            if (fileTypesArray.length > 0) {
                const firstFileType = fileTypesArray[0];
                setSelectedFileType(firstFileType);
                setSelectedFileTypeId(firstFileType._id);
                fetchTalukas(firstFileType._id);
            }

        } catch (err) {
            console.error("Error fetching file types:", err);
            toast({
                title: "Error",
                description: err.message || "Failed to load file types.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoadingFileTypes(false);
        }
    };

    const fetchTalukas = async (fileTypeId = null) => {
        const id = fileTypeId || selectedFileTypeId;

        if (!id) {
            toast({
                title: "Warning",
                description: "Please select a file type first",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const apiUrl = `${baseUrl}/recovery/taluka-targets?fileTypeId=${id}&currentFY=2026`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            }

            const response = await fetch(apiUrl, { headers });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("Talukas response:", result);

            if (result.data && result.data.tableConfig) {
                const { financialYear, tableConfig } = result.data;
                setFinancialYear(financialYear);
                const talukasArray = tableConfig.rows || [];
                setTalukas(talukasArray);

                const initialTargets = {};
                talukasArray.forEach((taluka) => {
                    const talukaId = taluka.talukaId;
                    if (talukaId) {
                        initialTargets[talukaId] = taluka.targetValue !== null ? taluka.targetValue : "";
                    }
                });
                setTargets(initialTargets);
            } else {
                setTalukas([]);
            }

        } catch (err) {
            console.error("Fetch error:", err);
            setError(err.message);
            toast({
                title: "Error",
                description: err.message || "Failed to load talukas.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFileTypeChange = (e) => {
        const selectedId = e.target.value;
        const selected = fileTypes.find(ft => ft._id === selectedId);

        setSelectedFileTypeId(selectedId);
        setSelectedFileType(selected || null);
        setTalukas([]);
        setTargets({});

        if (selectedId) {
            fetchTalukas(selectedId);
        }
    };

    const handleTargetChange = (talukaId, value) => {
        setTargets(prev => ({
            ...prev,
            [talukaId]: value
        }));
    };

    const handleApplyTarget = async (talukaId, talukaName, targetValue, currentTargetValue) => {
        if (targetValue === currentTargetValue) {
            toast({
                title: "No Change",
                description: "Target value is already set to this value",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!targetValue || targetValue <= 0) {
            toast({
                title: "Invalid Target",
                description: "Please enter a valid target value greater than 0",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!selectedFileTypeId) {
            toast({
                title: "Error",
                description: "Please select a file type first",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setSubmitting(prev => ({ ...prev, [talukaId]: true }));

        try {
            const targetApiUrl = `${baseUrl}/recovery/update_target_recovery`;

            const requestBody = {
                fileTypeId: selectedFileTypeId,
                currentFY: "2026",
                talukaId: talukaId,
                targetValue: parseFloat(targetValue),
                schemeId: schemeId,
                timestamp: new Date().toISOString(),
            };

            console.log("Setting target with data:", requestBody);

            const response = await fetch(targetApiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            toast({
                title: "Success",
                description: result.message || `Target of ${targetValue} set successfully for ${talukaName}`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            fetchTalukas();

        } catch (err) {
            console.error("Set target error:", err);
            toast({
                title: "Error",
                description: err.message || `Failed to set target for ${talukaName}`,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSubmitting(prev => ({ ...prev, [talukaId]: false }));
        }
    };

    if (loadingFileTypes) {
        return (
            <Container maxW="container.xl" py={8}>
                <Box textAlign="center" py={10}>
                    <Spinner size="xl" thickness="4px" color="blue.500" />
                    <Text mt={4}>Loading file types...</Text>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxW="container.xl" py={6} mt={20}>

            <VStack spacing={5} align="stretch">
                <HStack justify='space-between'>
                    <Heading as="h1" size="md" color="blue.600">
                        Set Recovery Targets
                    </Heading> {/* Financial Year Badge */}
                    {financialYear && (
                        <Flex justify="flex-end">
                            <Badge colorScheme="blue" fontSize="sm" p={2} borderRadius="md">
                                📅 Financial Year: {financialYear}
                            </Badge>
                        </Flex>
                    )}
                </HStack>


                {/* File Type Selection - Compact */}
                <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Flex gap={3} align="flex-end">
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                Select File Type
                            </Text>
                            <Select
                                placeholder="Choose file type"
                                value={selectedFileTypeId}
                                onChange={handleFileTypeChange}
                                size="md"
                                bg="white"
                            >
                                {fileTypes.map((fileType) => (
                                    <option key={fileType._id} value={fileType._id}>
                                        {fileType.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Button
                            colorScheme="blue"
                            onClick={() => fetchTalukas()}
                            isDisabled={!selectedFileTypeId}
                            isLoading={loading}
                            size="md"
                            px={6}
                        >
                            Load Talukas
                        </Button>
                    </Flex>
                </Box>

                {/* Selected File Type Info - Compact */}
                {selectedFileType && (
                    <Box p={2} bg="green.50" borderRadius="md">
                        <HStack spacing={2}>
                            <Badge colorScheme="green">Active</Badge>
                            <Text fontSize="sm" fontWeight="medium">
                                {selectedFileType.name}
                            </Text>
                        </HStack>
                    </Box>
                )}

                {/* Loading State */}
                {loading && (
                    <Box textAlign="center" py={8}>
                        <Spinner size="lg" thickness="3px" color="blue.500" />
                        <Text mt={3} fontSize="sm">Loading talukas...</Text>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert status="error" borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="sm">{error}</Text>
                    </Alert>
                )}

                {/* Talukas Table */}
                {!loading && !error && talukas.length > 0 && (
                    <Box overflowX="auto" maxH="500px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                        <Box minW="800px">
                            {/* Header - Sticky */}
                            <Box
                                display="grid"
                                gridTemplateColumns="60px 2fr 1.5fr 1.5fr 100px"
                                bg="green.400"
                                py={3}
                                px={4}
                                borderTopRadius="md"
                                fontSize="sm"
                                fontWeight="bold"
                                color="white"
                                position="sticky"
                                top={0}
                                zIndex={10}
                            >
                                <Text>SR.NO</Text>
                                <Text>Taluka Name</Text>
                                <Text>Target (in Lakhs)</Text>
                                <Text>Last Update</Text>
                                <Text textAlign="center">Action</Text>
                            </Box>

                            {/* Rows */}
                            <VStack spacing={0} align="stretch" gap={0}>
                                {talukas.map((taluka, index) => {
                                    const talukaId = taluka.talukaId;
                                    const talukaName = taluka.talukaName;
                                    const slNo = taluka.slNo;
                                    const existingTarget = taluka.targetValue;
                                    const currentTarget = targets[talukaId] !== undefined ? targets[talukaId] : (existingTarget !== null ? existingTarget : "");
                                    const isSubmitting = submitting[talukaId];
                                    const isTargetSet = existingTarget !== null && existingTarget > 0;
                                    const lastUpdate = taluka.lastUpdated || taluka.lastUpdate || "-";

                                    return (
                                        <Box
                                            key={talukaId}
                                            display="grid"
                                            gridTemplateColumns="60px 2fr 1.5fr 1.5fr 100px"
                                            alignItems="center"
                                            py={2}
                                            px={4}
                                            bg={index % 2 === 0 ? "white" : "gray.50"}
                                            _hover={{ bg: "blue.50" }}
                                            borderBottom="1px solid"
                                            borderColor="gray.100"
                                            fontSize="sm"
                                            transition="all 0.2s"
                                        >
                                            {/* Serial Number */}
                                            <Text color="gray.700" fontWeight="medium">
                                                {slNo}
                                            </Text>

                                            {/* Taluka Name */}
                                            <Text fontWeight="semibold" color="gray.800">
                                                {talukaName}
                                            </Text>

                                            {/* Target Input */}
                                            <Input
                                                type="number"
                                                placeholder="Enter target"
                                                value={currentTarget}
                                                onChange={(e) => handleTargetChange(talukaId, e.target.value)}
                                                disabled={isSubmitting}
                                                size="sm"
                                                height="35px"
                                                min="0"
                                                step="0.01"
                                                maxW="180px"
                                                bg={isTargetSet ? "green.50" : "white"}
                                                borderColor={isTargetSet ? "green.300" : "gray.300"}
                                                _focus={{
                                                    borderColor: "blue.500",
                                                    boxShadow: "0 0 0 1px #3182ce",
                                                }}
                                            />

                                            {/* Last Update */}
                                            <Text fontSize="sm" color="gray.500">
                                                {lastUpdate !== "-" ? new Date(lastUpdate).toLocaleDateString() : "-"}
                                            </Text>

                                            {/* Action Button */}
                                            <Flex justify="center">
                                                <Button
                                                    colorScheme={isTargetSet ? "orange" : "blue"}
                                                    onClick={() => handleApplyTarget(talukaId, talukaName, currentTarget, existingTarget)}
                                                    isLoading={isSubmitting}
                                                    loadingText={isTargetSet ? "Updating..." : "Applying..."}
                                                    size="xs"
                                                    height="30px"
                                                    width="85px"
                                                    fontSize="xs"
                                                    variant={isTargetSet ? "outline" : "solid"}
                                                >
                                                    {isTargetSet ? "Update" : "Apply"}
                                                </Button>
                                            </Flex>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </Box>
                    </Box>
                )}

                {/* No Talukas Message */}
                {!loading && !error && selectedFileTypeId && talukas.length === 0 && (
                    <Alert status="info" borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="sm">No talukas found for the selected file type</Text>
                    </Alert>
                )}

                {/* Select File Type Prompt */}
                {!selectedFileTypeId && !loading && !error && fileTypes.length > 0 && (
                    <Alert status="warning" borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="sm">Please select a file type to load talukas</Text>
                    </Alert>
                )}

                {/* No File Types Available */}
                {fileTypes.length === 0 && !loadingFileTypes && (
                    <Alert status="error" borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="sm">No file types found for the specified scheme</Text>
                    </Alert>
                )}
            </VStack>
        </Container>
    );
};

export default TargetSet;